import { folderContentsLabel, folderLevel, folderPath, longAgo, seriesSpans } from '@church/domain'
import { useServiceAccess } from '../access/useAccess.ts'
import { usePlans } from './useServices.ts'

/**
 * ForServices' Plans page (docs/13-ACCESS.md): the folders drilled into one
 * level at a time — a level's folders first, then its service types — and,
 * on a computer, each service type as a card with a table of its plans.
 * What each person sees and may change follows the access ladder: folders
 * with nothing visible inside are left out (except for their
 * Administrators, who fill them), "+ Add plan" is an Editor's, a service
 * type's settings an Administrator's.
 */

/** A day as plan dates are kept (YYYY-MM-DD), in the viewer's own calendar. */
function localDay(date: Date) {
  const pad = (n: number) => String(n).padStart(2, '0')
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`
}

/** One level of the folders (null: the top) as the Plans page shows it. */
export function useServiceFolderLevel(folderId: string | null) {
  const access = useServiceAccess()
  const administers = (id: string | null) => access.planningInFolder(id) === 'administrator'
  const level = folderLevel(access.folders, access.types, folderId, (folder) =>
    administers(folder.id),
  )
  return {
    /** The folders and service types have arrived. */
    isPending: !access.ready,
    /** False for a folder that is gone, or not theirs to see. */
    found: level.found,
    /** The folder itself; null at the top. */
    folder: level.path.at(-1) ?? null,
    /** Its ancestors and itself, outermost first, for the breadcrumbs. */
    path: level.path,
    /** Its folders, each with what is inside ("3 service types · 1 folder"). */
    folders: level.folders.map((card) => ({ ...card, label: folderContentsLabel(card) })),
    /** Its own service types. */
    types: level.types,
    /** The folder's settings — rename, move, delete, its Access — are its Administrators'. */
    canManageFolder: folderId !== null && administers(folderId),
    /** A new folder or service type here: an Administrator here (at the top, church-wide). */
    canAddHere: administers(folderId),
    /** "+ Add plan" on a service type: Editor there. */
    canPlan: (serviceTypeId: string) => access.can(serviceTypeId, 'editor'),
    /** A service type's own settings: Administrator there. */
    canConfigure: (serviceTypeId: string) => access.can(serviceTypeId, 'administrator'),
    /** Every service type they may add a plan to (a phone's New plan). */
    editorTypes: access.typesWhere('editor'),
  }
}

/** Where a service type sits: its folders, outermost first (for its breadcrumbs). */
export function useServiceTypePlace(serviceTypeId: string | null) {
  const access = useServiceAccess()
  const type = access.types.find((candidate) => candidate.id === serviceTypeId)
  return {
    type: type ?? null,
    folders: folderPath(access.folders, type?.folderId ?? null),
  }
}

/** Which plans a service type's card shows. */
export type PlansWhen = 'upcoming' | 'past'

/**
 * A service type's plans as its card's table reads them: upcoming soonest
 * first (from today) or past newest first, a page at a time. Each row knows
 * how many rows its series cell spans (`seriesSpans`), whether it is the next
 * plan (the accent bar), and its last change in words ("36 minutes ago").
 */
export function useServiceTypePlansTable(serviceTypeId: string, when: PlansWhen, pageSize = 8) {
  const now = new Date()
  const today = localDay(now)
  const yesterday = localDay(new Date(now.getFullYear(), now.getMonth(), now.getDate() - 1))
  const plans = usePlans(
    when === 'upcoming'
      ? { serviceTypeId, from: today, limit: pageSize }
      : { serviceTypeId, to: yesterday, order: 'desc', limit: pageSize },
  )
  const list = plans.data?.pages.flatMap((page) => page.items) ?? []
  const spans = seriesSpans(list)
  return {
    isPending: plans.isPending,
    error: plans.error,
    rows: list.map((plan, index) => ({
      plan,
      /** Rows its series cell spans: 0 under the first of a run, 1 for a plan alone. */
      seriesSpan: spans[index] ?? 1,
      /** The next plan ahead: the accent bar on its left. */
      next: when === 'upcoming' && index === 0,
      isToday: plan.date === today,
      /** "36 minutes ago". */
      updatedAgo: longAgo(new Date(plan.updatedAt), now),
    })),
    hasMore: plans.hasNextPage,
    loadingMore: plans.isFetchingNextPage,
    loadMore: () => void plans.fetchNextPage(),
  }
}
